import {
  useList as _useList,
  type APIConfiguration,
  Service,
  type SWRConfiguration,
  useGet,
  useGetWithCount,
  useListWithCount,
  useRead,
} from "@api/common";
import { chunk } from "lodash";
import { ListMessageStatus } from "./types";
import type {
  AnyKeys,
  BulkCreateParams,
  List,
  ListCondition,
  ListMember,
  ListMessage,
  ListMessageProtocol,
  ListParams,
  ListPreview,
  PopulatedListMember,
  PopulatedListMessage,
  ReadParams,
} from "./types";
import { getParamsWithPagination } from "./utils";

const LIST_MEMBER_CUSTOMER_POPULATE =
  "customer(firstName,lastName,phoneNumber,email,source,optout,properties,createdAt)";

export class ListService extends Service<List> {
  constructor() {
    super("/lists");
  }

  async buildQuery(body: AnyKeys<List>): Promise<{ conditions: ListCondition[] }> {
    return this.post(`${this.basePath}/query-builder`, body);
  }
  async createPreview(body: AnyKeys<List>): Promise<ListPreview> {
    return this.post(`${this.basePath}/preview`, body);
  }
  async createMembers(listId: string, body: { customers: string[] }) {
    return this.post(`${this.basePath}/${listId}/members`, body);
  }
  async deleteMember({ listId, memberId }: { listId: string; memberId: string }) {
    return this.delete(`${this.basePath}/${listId}/members/${memberId}`);
  }

  async createMessage(props: {
    list: string;
    body: string;
    status: typeof ListMessageStatus.queued | typeof ListMessageStatus.scheduled;
    media?: { url: string; contentType: string }[];
    scheduleDate?: string;
    protocols?: ListMessageProtocol[];
    subject?: string;
    email?: { templateId?: string; templateDoc?: Record<string, any> };
  }) {
    return this.post<ListMessage>("/list-messages", {
      list: props.list,
      body: props.body,
      status: props.status,
      media: props.media,
      scheduleDate: props.scheduleDate,
      protocols: props.protocols,
      subject: props.subject,
      email: props.email,
    });
  }

  async updateMessage(
    id: string,
    props: {
      list: string;
      body: string;
      status:
        | typeof ListMessageStatus.draft
        | typeof ListMessageStatus.scheduled
        | typeof ListMessageStatus.queued;
      media?: { url: string; contentType: string }[];
      scheduleDate?: string;
      protocols?: ListMessageProtocol[];
      subject?: string | null;
      email?: { templateId?: string; templateDoc?: Record<string, any> } | null;
    },
  ) {
    return this.put(`/list-messages/${id}`, {
      list: props.list,
      body: props.body,
      status: props.status,
      media: props.media,
      scheduleDate: props.scheduleDate,
      protocols: props.protocols,
      subject: props.subject,
      email: props.email,
    });
  }

  async deleteMessage(id: string) {
    return this.delete(`/list-messages/${id}`);
  }

  async bulkCreateMembers(
    listId: string,
    body: BulkCreateParams,
    callback?: (progress: number) => void,
  ): Promise<any> {
    const total = body.customers.length;
    let count = 0;
    for (const _customers of chunk(body.customers, 100)) {
      const { customers }: any = await this.post("/customers/bulk", {
        gym: body.gym,
        customers: _customers,
      });
      await this.createMembers(listId, { customers });
      count += _customers.length;
      callback?.(Math.round((count / total) * 100));
    }
  }
}

export const listService = new ListService();

export function useLists(params?: ListParams<List>, config?: APIConfiguration) {
  return _useList(listService, params, config);
}

export function useListsWithCount(params?: ListParams<List>, config?: SWRConfiguration) {
  return useListWithCount(listService, { ...getParamsWithPagination(params) }, config);
}

export function useList(id?: string, params?: ReadParams, config?: SWRConfiguration) {
  return useRead(listService, id, params, config);
}

export function useListMembers(
  id?: string,
  params?: ListParams<ListMember>,
  config?: SWRConfiguration,
) {
  return useGet<PopulatedListMember[]>(
    listService,
    id ? `${listService.basePath}/${id}/members` : null,
    { populate: LIST_MEMBER_CUSTOMER_POPULATE, ...params },
    config,
  );
}

export function useListMembersWithCount(
  id: string,
  params?: ListParams<ListMember>,
  config?: SWRConfiguration,
) {
  return useGetWithCount<PopulatedListMember[]>(
    listService,
    `${listService.basePath}/${id}/members`,
    {
      populate: LIST_MEMBER_CUSTOMER_POPULATE,
      ...getParamsWithPagination(params),
    },
    config,
  );
}

export function useListsMember(
  id: string,
  params?: ListParams<ListMember>,
  config?: SWRConfiguration,
) {
  return useGet<ListMember[]>(
    listService,
    "/list-members",
    {
      customer: id,
      populate: "customer(firstName,lastName,phoneNumber) list",
      ...params,
    },
    config,
  );
}

export function useListMessages(
  id: string,
  params?: ListParams<ListMessage>,
  config?: SWRConfiguration,
) {
  return useGet<ListMessage[]>(listService, "/list-messages", { ...params, list: id }, config);
}

export function useListMessage(id?: string, params?: ReadParams, config?: SWRConfiguration) {
  return useGet<PopulatedListMessage>(
    listService,
    id ? `/list-messages/${id}` : null,
    { populate: "list(name)", ...params },
    config,
  );
}
